import type { ChartSelectorConfig } from '@revelio/core';
import { type ChordTickLabelMode, FormatType } from '@revelio/replots';

import type { DateRangePresets } from '../../filters/constants/datePresets';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { getChartSelectorConfig } from '../ChartConfig/chart-selector-config';
import { getAllDimensionSourceOptions } from '../ChartConfig/utils/dimension-source-options';
import { isSelectableChartType } from '../ChartConfig/utils/is-selectable-chart-type';
import { getAvailableMetrics } from '../ChartConfig/utils/metric-mapping';
import { resolveNextSecondaryDimension } from '../ChartConfig/utils/resolve-next-secondary-dimension';
import { syncSlotValueToSnowflakeFields } from '../ChartConfig/utils/sync-slot-to-snowflake-field';
import {
  getColumnDimensionLabel,
  getPrimaryDimension,
} from '../ChartConfig/utils/table-columns';
import { reportPageHeight } from '../EditableGrid/utils/report-page-boundaries';
import { DEFAULT_GRID_ROWS } from './constants';
import { getChartTitle } from './get-chart-title';
import type { Page } from './types';
import type { ChartType } from './types/chart';
import type { SecondaryDimensionValues } from './types/dimension';
import type {
  ComparisonTransformation,
  DataNormalization,
  DataTransformation,
  DimensionSource,
  PlotFilter,
  PlotSortDirection,
  PositionedItem,
  SnowflakeConfig,
  TableColumnConfig,
} from './types/item';
import type { MetricValues } from './types/metric';

/* -------------------------------------------------------------------------- */
/*                              TabContent types                              */
/* -------------------------------------------------------------------------- */

type AddBlankItemAction = {
  type: 'ADD_BLANK_ITEM';
  payload: Pick<PositionedItem, 'position' | 'id'>;
};

type UpdateItemChartTypeAction = {
  type: 'UPDATE_ITEM_CHART_TYPE';
  payload: { id: string; chartType: ChartType };
};

type UpdateItemDefaultMetricOptionsAction = {
  type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS';
  payload: { id: string; defaultMetricOptions: MetricValues[] };
};

type UpdateItemSecondaryDimensionAction = {
  type: 'UPDATE_ITEM_SECONDARY_DIMENSION';
  payload: {
    id: string;
    secondaryDimension: SecondaryDimensionValues | undefined;
  };
};

type UpdateItemDimensionSourceAction = {
  type: 'UPDATE_ITEM_DIMENSION_SOURCE';
  payload: { id: string; dimensionSource: DimensionSource };
};

type RemoveItemDimensionSourceAction = {
  type: 'REMOVE_ITEM_DIMENSION_SOURCE';
  payload: { id: string };
};

type AddTableColumnAction = {
  type: 'ADD_TABLE_COLUMN';
  payload: { id: string; column: TableColumnConfig };
};

type UpdateTableColumnAction = {
  type: 'UPDATE_TABLE_COLUMN';
  payload: { id: string; column: TableColumnConfig };
};

type RemoveTableColumnAction = {
  type: 'REMOVE_TABLE_COLUMN';
  payload: { id: string; columnId: string };
};

type ReorderTableColumnsAction = {
  type: 'REORDER_TABLE_COLUMNS';
  payload: { id: string; columnIds: string[] };
};

type AddPlotFilterAction = {
  type: 'ADD_PLOT_FILTER';
  payload: { id: string; filter: PlotFilter };
};

type RemovePlotFilterAction = {
  type: 'REMOVE_PLOT_FILTER';
  payload: { plotId: string; filterIndex: number };
};

type UpdatePlotFilterAction = {
  type: 'UPDATE_PLOT_FILTER';
  payload: { plotId: string; filterIndex: number; filter: PlotFilter };
};

type UpdateItemGroupBySegmentAction = {
  type: 'UPDATE_ITEM_GROUP_BY_SEGMENT';
  payload: { id: string; groupBySegment: boolean };
};

type UpdateItemTextHtmlAction = {
  type: 'UPDATE_ITEM_TEXT_HTML';
  payload: { id: string; textHtml: string };
};

type UpdateItemTextVAlignAction = {
  type: 'UPDATE_ITEM_TEXT_VALIGN';
  payload: { id: string; textVAlign: 'start' | 'center' | 'end' };
};

type UpdateItemExcludeFromExecutiveSummaryAction = {
  type: 'UPDATE_ITEM_EXCLUDE_FROM_EXECUTIVE_SUMMARY';
  payload: { id: string; excludeFromExecutiveSummary: boolean };
};

type UpdateItemGaugeValueAction = {
  type: 'UPDATE_ITEM_GAUGE_VALUE';
  payload: { id: string; gaugeValue: string };
};

type UpdateItemGaugePrimaryTextAction = {
  type: 'UPDATE_ITEM_GAUGE_PRIMARY_TEXT';
  payload: { id: string; gaugePrimaryText: string };
};

type UpdateItemGaugePrimaryLabelAction = {
  type: 'UPDATE_ITEM_GAUGE_PRIMARY_LABEL';
  payload: { id: string; gaugePrimaryLabel: string };
};

type UpdateItemGaugeSubTextAction = {
  type: 'UPDATE_ITEM_GAUGE_SUB_TEXT';
  payload: { id: string; gaugeSubText: string };
};

type SetItemsAction = {
  type: 'SET_ITEMS';
  payload: PositionedItem[];
};

type SetGridAction = {
  type: 'SET_GRID';
  payload: { rows: number; columns?: number };
};

type DeleteItemAction = {
  type: 'DELETE_ITEM';
  payload: { id: string };
};

type DeleteItemsAction = {
  type: 'DELETE_ITEMS';
  payload: { ids: string[] };
};

type UpdateItemTitleAction = {
  type: 'UPDATE_ITEM_TITLE';
  payload: { id: string; title: string | null };
};

type UpdatePrimaryFilterAction = {
  type: 'UPDATE_PRIMARY_FILTER';
  payload: PrimaryFilterId;
};

type AddRowsAction = {
  type: 'ADD_ROWS';
  payload: { rows: number };
};

type AddPageAction = {
  type: 'ADD_PAGE';
  payload: { pageNumber: number };
};

type DeletePageAboveAction = {
  type: 'DELETE_PAGE_ABOVE';
  payload: { pageNumber: number };
};

type DeletePageBelowAction = {
  type: 'DELETE_PAGE_BELOW';
  payload: { pageNumber: number };
};

type RemoveRowsAction = {
  type: 'REMOVE_ROWS';
  payload: { rows: number };
};

type UpdateItemColorAction = {
  type: 'UPDATE_ITEM_COLOR';
  payload: { id: string; color: string };
};

type UpdateItemColorPaletteAction = {
  type: 'UPDATE_ITEM_COLOR_PALETTE';
  payload: { id: string; colorPalette: string[] };
};

type UpdateItemShowDataLabelsAction = {
  type: 'UPDATE_ITEM_SHOW_DATA_LABELS';
  payload: { id: string; showDataLabels: boolean };
};

type UpdateItemSortAction = {
  type: 'UPDATE_ITEM_SORT';
  payload: { id: string; sort: PlotSortDirection | null };
};

type UpdateItemBarAxisRangeAction = {
  type: 'UPDATE_ITEM_BAR_AXIS_RANGE';
  payload: { id: string; maxValue?: number; minValue?: number };
};

type UpdateItemOrientationAction = {
  type: 'UPDATE_ITEM_ORIENTATION';
  payload: { id: string; orientation: 'horizontal' | 'vertical' };
};

type UpdateItemShowZebraStripeAction = {
  type: 'UPDATE_ITEM_SHOW_ZEBRA_STRIPE';
  payload: { id: string; showZebraStripe: boolean };
};

type UpdateItemHideOtherAction = {
  type: 'UPDATE_ITEM_HIDE_OTHER';
  payload: { id: string; hideOther: boolean };
};

type UpdateItemShowQuartilesAction = {
  type: 'UPDATE_ITEM_SHOW_QUARTILES';
  payload: { id: string; showQuartiles: boolean };
};

type UpdateItemEnableRowSelectionAction = {
  type: 'UPDATE_ITEM_ENABLE_ROW_SELECTION';
  payload: { id: string; enableRowSelection: boolean };
};

type UpdateItemNormalizationAction = {
  type: 'UPDATE_ITEM_NORMALIZATION';
  payload: { id: string; normalization: DataNormalization | undefined };
};

type UpdateItemTransformationAction = {
  type: 'UPDATE_ITEM_TRANSFORMATION';
  payload: { id: string; transformation: DataTransformation };
};

type SetReportParametersAction = {
  type: 'SET_REPORT_PARAMETERS';
  payload: ReportParameterConfig[] | undefined;
};

type AddReportParameterAction = {
  type: 'ADD_REPORT_PARAMETER';
  payload: { parameter: ReportParameterConfig };
};

type UpdateReportParameterAction = {
  type: 'UPDATE_REPORT_PARAMETER';
  payload: {
    parameterId: string;
    updates: Partial<ReportParameterConfig>;
  };
};

type RemoveReportParameterAction = {
  type: 'REMOVE_REPORT_PARAMETER';
  payload: { parameterId: string };
};

type UpdateReportTitleAction = {
  type: 'UPDATE_REPORT_TITLE';
  payload: { title: string };
};

type UpdateReportDescriptionAction = {
  type: 'UPDATE_REPORT_DESCRIPTION';
  payload: { description: string };
};
type AddItemPlotParameterAction = {
  type: 'ADD_ITEM_PLOT_PARAMETER';
  payload: { id: string; parameterId: string };
};

type RemoveItemPlotParameterAction = {
  type: 'REMOVE_ITEM_PLOT_PARAMETER';
  payload: { id: string; parameterId: string };
};

type UpdateItemParameterGroupingAction = {
  type: 'UPDATE_ITEM_PARAMETER_GROUPING';
  payload: {
    id: string;
    parameterId: string;
    grouped: boolean;
  };
};

type UpdateItemComparisonTransformationAction = {
  type: 'UPDATE_ITEM_COMPARISON_TRANSFORMATION';
  payload: {
    id: string;
    comparisonTransformation: ComparisonTransformation | undefined;
  };
};

type UpdateItemDivergingBarComboAction = {
  type: 'UPDATE_ITEM_DIVERGING_BAR_COMBO';
  payload: {
    id: string;
    divergingBarCombo: {
      comparisonLabel?: string;
      differenceLabel?: string;
      leftHeaderLabel?: string;
      rightHeaderLabel?: string;
    };
  };
};

type UpdateItemDivergingBarAction = {
  type: 'UPDATE_ITEM_DIVERGING_BAR';
  payload: {
    id: string;
    divergingBar: {
      arrowOrientation?: 'horizontal' | 'vertical';
      positiveLabel?: string;
      negativeLabel?: string;
    };
  };
};

type UpdateItemDisplayAsAction = {
  type: 'UPDATE_ITEM_DISPLAY_AS';
  payload: { id: string; displayAs: 'value' | 'share' };
};

type UpdateItemTreemapSizeTransformAction = {
  type: 'UPDATE_ITEM_TREEMAP_SIZE_TRANSFORM';
  payload: {
    id: string;
    treemapSizeTransform: Exclude<DataTransformation, 'log'>;
  };
};

type UpdateItemChordOptionsAction = {
  type: 'UPDATE_ITEM_CHORD_OPTIONS';
  payload: {
    id: string;
    showTickLabels?: boolean;
    tickLabelMode?: ChordTickLabelMode;
  };
};

type UpdateItemShowDiffLabelAction = {
  type: 'UPDATE_ITEM_SHOW_DIFF_LABEL';
  payload: { id: string; showDiffLabel: boolean };
};

type UpdateItemShowLegendAction = {
  type: 'UPDATE_ITEM_SHOW_LEGEND';
  payload: { id: string; showLegend: boolean };
};

type UpdateItemAdaptiveAction = {
  type: 'UPDATE_ITEM_ADAPTIVE';
  payload: { id: string; adaptive: boolean };
};

type UpdateItemCurveSmoothnessAction = {
  type: 'UPDATE_ITEM_CURVE_SMOOTHNESS';
  payload: { id: string; curveSmoothness: number };
};

type UpdateItemFontSizeAction = {
  type: 'UPDATE_ITEM_FONT_SIZE';
  payload: { id: string; fontSize: number };
};

type UpdateItemDistributionCutoffAction = {
  type: 'UPDATE_ITEM_DISTRIBUTION_CUTOFF';
  payload: {
    id: string;
    distributionCutoffLower?: number;
    distributionCutoffUpper?: number;
  };
};

type UpdateItemFormatAction = {
  type: 'UPDATE_ITEM_FORMAT';
  payload: { id: string; format?: FormatType };
};

type SetSecondaryFiltersAction = {
  type: 'SET_SECONDARY_FILTERS';
  payload: { filters: SecondaryFilterConfig[] };
};

type UpdateItemDataSourceAction = {
  type: 'UPDATE_ITEM_DATASOURCE';
  payload: { id: string; dataSource: 'mw-gql' | 'snowflake' };
};

type UpdateItemSnowflakeConfigAction = {
  type: 'UPDATE_ITEM_SNOWFLAKE_CONFIG';
  payload: {
    id: string;
    snowflakeConfig: SnowflakeConfig;
    selectorOverrides?: Partial<ChartSelectorConfig>;
  };
};

type UpdateItemSelectorOverridesAction = {
  type: 'UPDATE_ITEM_SELECTOR_OVERRIDES';
  payload: {
    id: string;
    selectorOverrides: Partial<ChartSelectorConfig>;
  };
};

export type TabContentAction = (
  | AddBlankItemAction
  | UpdateItemChartTypeAction
  | UpdateItemDefaultMetricOptionsAction
  | UpdateItemSecondaryDimensionAction
  | UpdateItemSecondaryDimensionAction
  | UpdateItemDimensionSourceAction
  | RemoveItemDimensionSourceAction
  | AddTableColumnAction
  | UpdateTableColumnAction
  | RemoveTableColumnAction
  | ReorderTableColumnsAction
  | AddPlotFilterAction
  | RemovePlotFilterAction
  | UpdatePlotFilterAction
  | UpdateItemGroupBySegmentAction
  | UpdateItemTextHtmlAction
  | UpdateItemTextVAlignAction
  | UpdateItemExcludeFromExecutiveSummaryAction
  | UpdateItemGaugeValueAction
  | UpdateItemGaugePrimaryTextAction
  | UpdateItemGaugePrimaryLabelAction
  | UpdateItemGaugeSubTextAction
  | SetItemsAction
  | SetGridAction
  | DeleteItemAction
  | DeleteItemsAction
  | UpdateItemTitleAction
  | UpdatePrimaryFilterAction
  | AddRowsAction
  | AddPageAction
  | DeletePageAboveAction
  | DeletePageBelowAction
  | RemoveRowsAction
  | UpdateItemColorAction
  | UpdateItemColorPaletteAction
  | UpdateItemColorPaletteAction
  | UpdateItemShowDataLabelsAction
  | UpdateItemSortAction
  | UpdateItemBarAxisRangeAction
  | UpdateItemOrientationAction
  | UpdateItemShowZebraStripeAction
  | UpdateItemHideOtherAction
  | UpdateItemShowQuartilesAction
  | UpdateItemEnableRowSelectionAction
  | UpdateItemNormalizationAction
  | UpdateItemTransformationAction
  | SetReportParametersAction
  | AddReportParameterAction
  | UpdateReportParameterAction
  | RemoveReportParameterAction
  | UpdateReportTitleAction
  | UpdateReportDescriptionAction
  | AddItemPlotParameterAction
  | UpdateItemDivergingBarAction
  | RemoveItemPlotParameterAction
  | UpdateItemParameterGroupingAction
  | UpdateItemComparisonTransformationAction
  | UpdateItemDivergingBarComboAction
  | UpdateItemDisplayAsAction
  | UpdateItemTreemapSizeTransformAction
  | UpdateItemChordOptionsAction
  | UpdateItemShowDiffLabelAction
  | UpdateItemShowLegendAction
  | UpdateItemAdaptiveAction
  | UpdateItemCurveSmoothnessAction
  | UpdateItemFontSizeAction
  | UpdateItemDistributionCutoffAction
  | UpdateItemFormatAction
  | SetSecondaryFiltersAction
  | UpdateItemDataSourceAction
  | UpdateItemSnowflakeConfigAction
  | UpdateItemSelectorOverridesAction
) & { replacePreviousHistorySnapshot?: boolean };

export interface PrimaryFilterConfig {
  id: PrimaryFilterId;
  tagColors?: boolean;
  selectionLimit: number;
  selectionDefault: number;
}

export interface SecondaryFilterConfig {
  id: FilterId;
}

export type ReportParameterConfig = {
  parameterId: string;
  filterId: FilterId;
  isPrimary: boolean;
  name: string;
  selectionLimit?: number;
  isOptional?: boolean;
  allowMultiLevelSelect?: boolean;
};

export type TabGrid = {
  rows: number;
  columns: number;
};

export type TabContent = {
  id: string;
  items: PositionedItem[];
  grid: TabGrid;
  primaryFilter?: PrimaryFilterConfig;
  reportParameters?: ReportParameterConfig[];
  secondaryFilters: SecondaryFilterConfig[];
  dateRangeDefault?: (typeof DateRangePresets)[number]['value'];
  title?: string;
  description?: string;
};

/* --------------------------- Page reducer helpers -------------------------- */

function deriveComparisonSlotValue(dimensionSource: DimensionSource): string {
  if (dimensionSource === 'primary') {
    return '';
  }
  return dimensionSource;
}

/** Clears template/persisted comparison dimension when the new metric no longer supports it. */
function getNextDimensionSourceAfterMetricChange({
  primaryFilterId,
  chartType,
  metric,
  previousDimensionSource,
}: {
  primaryFilterId: PrimaryFilterId;
  chartType: ChartType | undefined;
  metric: MetricValues | undefined;
  previousDimensionSource: DimensionSource | undefined;
}): DimensionSource | undefined {
  if (!previousDimensionSource) {
    return undefined;
  }

  if (
    chartType === 'bar' &&
    primaryFilterId === FilterId.ROLE &&
    metric !== undefined &&
    metric !== 'headcount'
  ) {
    return undefined;
  }

  if (metric === undefined) {
    return previousDimensionSource;
  }

  const options = getAllDimensionSourceOptions({
    chartType,
    primaryFilterId,
    metric,
  });
  if (options.length === 0) {
    return undefined;
  }
  const allowed = new Set(options.map((o) => o.value));
  return allowed.has(previousDimensionSource)
    ? previousDimensionSource
    : undefined;
}

const updateItem = (
  items: PositionedItem[],
  id: string,
  updateFn: (item: PositionedItem) => PositionedItem
): PositionedItem[] => {
  return items.map((item) => {
    if (item.id !== id) return item;
    return updateFn(item);
  });
};

/** Merge a partial plot into the item's plot; use for simple top-level plot property updates. */
const updateItemPlot = (
  items: PositionedItem[],
  id: string,
  plotPatch: Partial<NonNullable<PositionedItem['plot']>>
): PositionedItem[] =>
  updateItem(items, id, (item) => ({
    ...item,
    plot: { ...item.plot, ...plotPatch },
  }));

const getDefaultMetricOptionsForNewChart = ({
  chartType,
  primaryFilterId,
}: {
  chartType: ChartType;
  primaryFilterId: PrimaryFilterId;
}): MetricValues[] => {
  if (!isSelectableChartType(chartType)) return [];

  const selectorConfig = getChartSelectorConfig(chartType);
  if (selectorConfig?.metricMode !== 'single') return [];

  const firstMetric = getAvailableMetrics({
    primaryFilterId,
    chartType,
  })[0]?.options[0]?.value;

  return firstMetric === undefined ? [] : [firstMetric];
};

const applyDefaultMetricOptionsToItem = ({
  item,
  defaultMetricOptions,
  primaryFilterId,
}: {
  item: PositionedItem;
  defaultMetricOptions: MetricValues[];
  primaryFilterId: PrimaryFilterId;
}): PositionedItem => {
  const isTitleManuallySet = item.isTitleManuallySet ?? false;
  const chartType = item.plot?.chartType;
  const currentSecondary = item.plot?.secondaryDimension;

  const nextSecondary = resolveNextSecondaryDimension({
    chartType,
    primaryFilterId,
    metrics: defaultMetricOptions,
    currentSecondary,
  });

  const secondaryChanged = nextSecondary !== currentSecondary;

  const newMetric = defaultMetricOptions[0];
  const nextDimensionSource = getNextDimensionSourceAfterMetricChange({
    primaryFilterId,
    chartType,
    metric: newMetric,
    previousDimensionSource: item.plot?.dimensionSource,
  });

  const updatedPlot: NonNullable<PositionedItem['plot']> = (() => {
    let basePlot: NonNullable<PositionedItem['plot']> = {
      ...item.plot,
      defaultMetricOptions,
      dimensionSource: nextDimensionSource,
      ...(secondaryChanged ? { secondaryDimension: nextSecondary } : {}),
    };

    if (
      nextDimensionSource === undefined &&
      item.plot?.dimensionSource &&
      basePlot.dataSource === 'snowflake' &&
      basePlot.snowflakeConfig?.fields?.length
    ) {
      basePlot = {
        ...basePlot,
        snowflakeConfig: {
          ...basePlot.snowflakeConfig,
          fields: syncSlotValueToSnowflakeFields(
            basePlot.snowflakeConfig.fields,
            'comparison',
            ''
          ),
        },
      };
    }

    if (!secondaryChanged) return basePlot;

    const displayAdjustedPlot =
      item.plot?.displayAs === 'share' &&
      (!nextSecondary || nextSecondary === 'timeseries')
        ? { ...basePlot, displayAs: 'value' as const }
        : basePlot;

    if (
      displayAdjustedPlot.dataSource === 'snowflake' &&
      nextSecondary &&
      displayAdjustedPlot.snowflakeConfig?.fields?.length
    ) {
      return {
        ...displayAdjustedPlot,
        snowflakeConfig: {
          ...displayAdjustedPlot.snowflakeConfig,
          fields: syncSlotValueToSnowflakeFields(
            displayAdjustedPlot.snowflakeConfig.fields,
            'segment',
            nextSecondary
          ),
        },
      };
    }

    return displayAdjustedPlot;
  })();

  const newItem = {
    ...item,
    plot: updatedPlot,
  };

  return {
    ...newItem,
    ...(!isTitleManuallySet &&
      defaultMetricOptions[0] && { title: getChartTitle(newItem) }),
  };
};

const applyTabContentAction = (
  state: TabContent,
  action: TabContentAction
): TabContent => {
  switch (action.type) {
    case 'ADD_BLANK_ITEM': {
      const { position, id } = action.payload;
      const newItem: PositionedItem = {
        position,
        id: id ?? crypto.randomUUID(),
        title: '',
      };
      return { ...state, items: [...state.items, newItem] };
    }
    case 'SET_REPORT_PARAMETERS': {
      const params = action.payload;
      return {
        ...state,
        reportParameters: params,
      };
    }
    case 'ADD_REPORT_PARAMETER': {
      const { parameter } = action.payload;
      const next = [...(state.reportParameters ?? []), parameter];
      return {
        ...state,
        reportParameters: next,
      };
    }
    case 'UPDATE_REPORT_PARAMETER': {
      const { parameterId, updates } = action.payload;
      const updated = (state.reportParameters ?? []).map((p) =>
        p.parameterId === parameterId ? { ...p, ...updates } : p
      );
      return {
        ...state,
        reportParameters: updated,
      };
    }
    case 'REMOVE_REPORT_PARAMETER': {
      const { parameterId } = action.payload;
      const filtered = (state.reportParameters ?? []).filter(
        (p) => p.parameterId !== parameterId
      );
      return {
        ...state,
        reportParameters: filtered,
      };
    }
    case 'UPDATE_ITEM_CHART_TYPE': {
      const { id, chartType } = action.payload;
      const primaryFilterId = state.primaryFilter?.id ?? FilterId.COMPANY;
      const primaryReportParameterId =
        (state.reportParameters ?? []).find((p) => p.isPrimary)?.parameterId ??
        null;
      const newItems = updateItem(state.items, id, (item) => {
        const isNewChart = !item.plot;
        const shouldInitTableColumns =
          chartType === 'table' && !item.plot?.tableColumns?.length;
        const initialTableColumns = (): TableColumnConfig[] => {
          const dimension = getPrimaryDimension(primaryFilterId);
          return [
            {
              id: crypto.randomUUID(),
              kind: 'dimension',
              dimension,
              alignment: 'left',
              label: getColumnDimensionLabel(dimension),
            },
          ];
        };
        const updatedPlot = (() => {
          // When a new chart is created, add the primary parameter by default
          if (isNewChart && primaryReportParameterId) {
            return {
              chartType,
              format: FormatType.DECIMAL,
              plotParameters: [
                { parameterId: primaryReportParameterId, grouped: false },
              ],
              ...(chartType === 'table' && { enableRowSelection: false }),
              ...(shouldInitTableColumns && {
                tableColumns: initialTableColumns(),
              }),
            };
          }
          return {
            ...item.plot,
            chartType,
            ...(isNewChart && { format: FormatType.DECIMAL }),
            ...(isNewChart &&
              chartType === 'table' && { enableRowSelection: false }),
            ...(shouldInitTableColumns && {
              tableColumns: initialTableColumns(),
            }),
          };
        })();
        if (chartType !== 'line' && item.plot?.displayAs === 'share') {
          updatedPlot.displayAs = 'value';
        }
        const updatedItem = {
          ...item,
          plot: updatedPlot,
        };
        const defaultMetricOptions = (() => {
          if (isNewChart) {
            return getDefaultMetricOptionsForNewChart({
              chartType,
              primaryFilterId,
            });
          }

          if (!isSelectableChartType(chartType)) {
            return [];
          }

          const selectorConfig = getChartSelectorConfig(chartType);
          if (selectorConfig?.metricMode !== 'single') {
            return [];
          }

          const availableMetrics = getAvailableMetrics({
            primaryFilterId,
            chartType,
          })
            .flatMap((group) => group.options)
            .map((option) => option.value);

          const currentMetric = item.plot?.defaultMetricOptions?.[0];
          if (
            currentMetric !== undefined &&
            availableMetrics.includes(currentMetric)
          ) {
            return [];
          }

          const defaultMetric = availableMetrics[0];
          return defaultMetric === undefined ? [] : [defaultMetric];
        })();

        const nextMetric =
          defaultMetricOptions[0] ?? item.plot?.defaultMetricOptions?.[0];
        const nextDimensionSource = getNextDimensionSourceAfterMetricChange({
          primaryFilterId,
          chartType,
          metric: nextMetric,
          previousDimensionSource: item.plot?.dimensionSource,
        });
        const dimensionSourceChanged =
          nextDimensionSource !== item.plot?.dimensionSource;

        const currentSecondary = item.plot?.secondaryDimension;
        const nextSecondary = resolveNextSecondaryDimension({
          chartType,
          primaryFilterId,
          metrics: nextMetric ? [nextMetric] : [],
          currentSecondary,
        });
        const secondaryChanged = nextSecondary !== currentSecondary;

        if (
          defaultMetricOptions.length === 0 &&
          !dimensionSourceChanged &&
          !secondaryChanged
        ) {
          return updatedItem;
        }

        let itemToProcess: PositionedItem = updatedItem;
        if (defaultMetricOptions.length > 0) {
          itemToProcess = applyDefaultMetricOptionsToItem({
            item: updatedItem,
            defaultMetricOptions,
            primaryFilterId,
          });
        }

        const basePlot = itemToProcess.plot;
        if (!basePlot) {
          return itemToProcess;
        }

        const isTitleManuallySet = itemToProcess.isTitleManuallySet ?? false;
        const plotWithDimension: NonNullable<PositionedItem['plot']> = (() => {
          const baseUpdated = {
            ...basePlot,
            dimensionSource: nextDimensionSource,
            ...(secondaryChanged ? { secondaryDimension: nextSecondary } : {}),
          };

          const withDisplayAdjusted: NonNullable<PositionedItem['plot']> =
            baseUpdated.displayAs === 'share' &&
            (baseUpdated.secondaryDimension === undefined ||
              baseUpdated.secondaryDimension === 'timeseries')
              ? { ...baseUpdated, displayAs: 'value' as const }
              : baseUpdated;

          if (
            withDisplayAdjusted.dataSource !== 'snowflake' ||
            !withDisplayAdjusted.snowflakeConfig?.fields?.length
          ) {
            return withDisplayAdjusted;
          }

          const comparisonSyncValue =
            nextDimensionSource === undefined
              ? ''
              : deriveComparisonSlotValue(nextDimensionSource);
          const fieldsAfterComparison = syncSlotValueToSnowflakeFields(
            withDisplayAdjusted.snowflakeConfig.fields,
            'comparison',
            comparisonSyncValue
          );
          const finalFields = secondaryChanged
            ? syncSlotValueToSnowflakeFields(
                fieldsAfterComparison,
                'segment',
                nextSecondary ?? ''
              )
            : fieldsAfterComparison;

          return {
            ...withDisplayAdjusted,
            snowflakeConfig: {
              ...withDisplayAdjusted.snowflakeConfig,
              fields: finalFields,
            },
          };
        })();

        const newItem = { ...itemToProcess, plot: plotWithDimension };
        if (isTitleManuallySet) {
          return newItem;
        }

        const metricForTitle = plotWithDimension.defaultMetricOptions?.[0];
        if (!metricForTitle) {
          return newItem;
        }

        const metricLabel = getChartTitle({
          ...newItem,
          plot: { ...plotWithDimension, secondaryDimension: undefined },
        });

        if (
          nextDimensionSource === undefined ||
          nextDimensionSource === 'primary'
        ) {
          return {
            ...newItem,
            title: metricLabel,
          };
        }

        const dimensionSourceLabel = getAllDimensionSourceOptions({
          chartType: plotWithDimension.chartType,
          primaryFilterId,
          metric: metricForTitle,
        }).find((option) => option.value === nextDimensionSource)?.label;

        return {
          ...newItem,
          ...(dimensionSourceLabel !== undefined && {
            title:
              metricForTitle === 'headcount'
                ? dimensionSourceLabel
                : `${dimensionSourceLabel} - ${metricLabel}`,
          }),
        };
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS': {
      const { id, defaultMetricOptions } = action.payload;
      const primaryFilterId = state.primaryFilter?.id ?? FilterId.COMPANY;
      const newItems = updateItem(state.items, id, (item) =>
        applyDefaultMetricOptionsToItem({
          item,
          defaultMetricOptions,
          primaryFilterId,
        })
      );
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SECONDARY_DIMENSION': {
      const { id, secondaryDimension } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const isTitleManuallySet = item.isTitleManuallySet ?? false;
        const updatedPlot = { ...item.plot, secondaryDimension };
        if (
          item.plot?.displayAs === 'share' &&
          (!secondaryDimension || secondaryDimension === 'timeseries')
        ) {
          updatedPlot.displayAs = 'value';
        }

        if (
          updatedPlot.dataSource === 'snowflake' &&
          secondaryDimension &&
          updatedPlot.snowflakeConfig?.fields?.length
        ) {
          updatedPlot.snowflakeConfig = {
            ...updatedPlot.snowflakeConfig,
            fields: syncSlotValueToSnowflakeFields(
              updatedPlot.snowflakeConfig.fields,
              'segment',
              secondaryDimension
            ),
          };
        }

        const newItem = {
          ...item,
          plot: updatedPlot,
        };
        return {
          ...newItem,
          ...(!isTitleManuallySet && { title: getChartTitle(newItem) }),
        };
      });
      return { ...state, items: newItems };
    }
    case 'ADD_TABLE_COLUMN':
    case 'UPDATE_TABLE_COLUMN':
    case 'REMOVE_TABLE_COLUMN':
    case 'REORDER_TABLE_COLUMNS': {
      const { id } = action.payload;
      const primaryFilterId = state.primaryFilter?.id;
      const newItems = updateItem(state.items, id, (item) => {
        const current = item.plot?.tableColumns ?? [];
        const tableColumns = ((): TableColumnConfig[] => {
          switch (action.type) {
            case 'ADD_TABLE_COLUMN':
              return [...current, action.payload.column];
            case 'UPDATE_TABLE_COLUMN':
              return current.map((c) =>
                c.id === action.payload.column.id ? action.payload.column : c
              );
            case 'REMOVE_TABLE_COLUMN':
              return current.filter((c) => c.id !== action.payload.columnId);
            case 'REORDER_TABLE_COLUMNS': {
              const byId = new Map(current.map((c) => [c.id, c]));
              return action.payload.columnIds
                .map((cid) => byId.get(cid))
                .filter((c): c is TableColumnConfig => c !== undefined);
            }
          }
        })();

        const newItem = {
          ...item,
          plot: { ...item.plot, tableColumns },
        };
        return {
          ...newItem,
          ...(!(item.isTitleManuallySet ?? false) && {
            title: getChartTitle(newItem, primaryFilterId),
          }),
        };
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DIMENSION_SOURCE': {
      const { id, dimensionSource } = action.payload;
      const primaryFilterId = state.primaryFilter?.id ?? FilterId.COMPANY;
      const newItems = updateItem(state.items, id, (item) => {
        const isTitleManuallySet = item.isTitleManuallySet ?? false;
        const updatedPlot = {
          ...item.plot,
          dimensionSource,
        };

        if (
          updatedPlot.chartType === 'stacked-bar' &&
          dimensionSource === 'timeseries'
        ) {
          updatedPlot.defaultMetricOptions = ['headcount'];
          updatedPlot.groupBySegment = false;
        }

        if (
          updatedPlot.dataSource === 'snowflake' &&
          updatedPlot.snowflakeConfig?.fields?.length
        ) {
          const comparisonValue = deriveComparisonSlotValue(dimensionSource);
          updatedPlot.snowflakeConfig = {
            ...updatedPlot.snowflakeConfig,
            fields: syncSlotValueToSnowflakeFields(
              updatedPlot.snowflakeConfig.fields,
              'comparison',
              comparisonValue
            ),
          };
        }

        const newItem = { ...item, plot: updatedPlot };
        if (isTitleManuallySet) {
          return newItem;
        }

        const metric = updatedPlot.defaultMetricOptions?.[0];
        if (!metric) {
          return newItem;
        }
        const metricLabel = getChartTitle({
          ...newItem,
          plot: { ...updatedPlot, secondaryDimension: undefined },
        });

        if (dimensionSource === 'primary') {
          return {
            ...newItem,
            title: metricLabel,
          };
        }

        const dimensionSourceLabel = getAllDimensionSourceOptions({
          chartType: updatedPlot.chartType,
          primaryFilterId,
          metric,
        }).find((option) => option.value === dimensionSource)?.label;

        return {
          ...newItem,
          ...(dimensionSourceLabel !== undefined && {
            title:
              metric === 'headcount'
                ? dimensionSourceLabel
                : `${dimensionSourceLabel} - ${metricLabel}`,
          }),
        };
      });
      return { ...state, items: newItems };
    }
    case 'REMOVE_ITEM_DIMENSION_SOURCE': {
      const { id } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const isTitleManuallySet = item.isTitleManuallySet ?? false;
        const updatedPlot = {
          ...item.plot,
          dimensionSource: undefined,
        };

        if (
          updatedPlot.dataSource === 'snowflake' &&
          updatedPlot.snowflakeConfig?.fields?.length
        ) {
          updatedPlot.snowflakeConfig = {
            ...updatedPlot.snowflakeConfig,
            fields: syncSlotValueToSnowflakeFields(
              updatedPlot.snowflakeConfig.fields,
              'comparison',
              ''
            ),
          };
        }

        const newItem = { ...item, plot: updatedPlot };
        if (isTitleManuallySet) {
          return newItem;
        }

        const metricLabel = getChartTitle({
          ...newItem,
          plot: { ...updatedPlot, secondaryDimension: undefined },
        });

        return {
          ...newItem,
          ...(metricLabel && { title: metricLabel }),
        };
      });
      return { ...state, items: newItems };
    }
    case 'ADD_PLOT_FILTER': {
      const { id, filter } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          plotFilters: [...(item.plot?.plotFilters ?? []), filter],
        },
      }));
      return { ...state, items: newItems };
    }
    case 'REMOVE_PLOT_FILTER': {
      const { plotId, filterIndex } = action.payload;
      const newItems = updateItem(state.items, plotId, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          plotFilters: item.plot?.plotFilters?.filter(
            (_, i) => i !== filterIndex
          ),
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_PLOT_FILTER': {
      const { plotId, filterIndex, filter } = action.payload;
      const newItems = updateItem(state.items, plotId, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          plotFilters: item.plot?.plotFilters?.map((f, i) =>
            i === filterIndex ? filter : f
          ),
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_GROUP_BY_SEGMENT': {
      const { id, groupBySegment } = action.payload;
      const newItems = updateItemPlot(state.items, id, { groupBySegment });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_TEXT_HTML': {
      const { id, textHtml } = action.payload;
      const newItems = updateItemPlot(state.items, id, { textHtml });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_TEXT_VALIGN': {
      const { id, textVAlign } = action.payload;
      const newItems = updateItemPlot(state.items, id, { textVAlign });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_EXCLUDE_FROM_EXECUTIVE_SUMMARY': {
      const { id, excludeFromExecutiveSummary } = action.payload;
      const newItems = updateItemPlot(state.items, id, {
        excludeFromExecutiveSummary,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_GAUGE_VALUE': {
      const { id, gaugeValue } = action.payload;
      const newItems = updateItemPlot(state.items, id, { gaugeValue });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_GAUGE_PRIMARY_TEXT': {
      const { id, gaugePrimaryText } = action.payload;
      const newItems = updateItemPlot(state.items, id, {
        gaugePrimaryText,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_GAUGE_PRIMARY_LABEL': {
      const { id, gaugePrimaryLabel } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: { ...item.plot, gaugePrimaryLabel },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_GAUGE_SUB_TEXT': {
      const { id, gaugeSubText } = action.payload;
      const newItems = updateItemPlot(state.items, id, { gaugeSubText });
      return { ...state, items: newItems };
    }
    case 'SET_ITEMS': {
      return { ...state, items: action.payload };
    }
    case 'SET_GRID': {
      const { rows, columns } = action.payload;
      const validRows =
        Number.isInteger(rows) && rows > 0 ? rows : state.grid.rows;
      const validColumns =
        columns !== undefined && Number.isInteger(columns) && columns > 0
          ? columns
          : state.grid.columns;
      return {
        ...state,
        grid: { rows: validRows, columns: validColumns },
      };
    }
    case 'DELETE_ITEM': {
      const { id } = action.payload;
      const newItems = state.items.filter((item) => item.id !== id);
      return { ...state, items: newItems };
    }
    case 'DELETE_ITEMS': {
      const { ids } = action.payload;
      const idSet = new Set(ids);
      const newItems = state.items.filter((item) => !idSet.has(item.id));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_TITLE': {
      const { id, title } = action.payload;
      const newItems = state.items.map((item) =>
        item.id === id ? { ...item, title, isTitleManuallySet: true } : item
      );
      return { ...state, items: newItems };
    }
    case 'UPDATE_PRIMARY_FILTER': {
      if (!state.primaryFilter) {
        return state;
      }
      return {
        ...state,
        primaryFilter: { ...state.primaryFilter, id: action.payload },
      };
    }
    case 'ADD_ROWS': {
      const { rows } = action.payload;
      return {
        ...state,
        grid: { ...state.grid, rows: state.grid.rows + rows },
      };
    }
    case 'ADD_PAGE': {
      const { pageNumber } = action.payload;

      const newPageRowIndex = pageNumber * reportPageHeight();
      const shiftedItems = state.items.map((item) => {
        if (item.position.y < newPageRowIndex) return item;
        return {
          ...item,
          position: {
            ...item.position,
            y: item.position.y + reportPageHeight(),
          },
        };
      });

      return {
        ...state,
        items: shiftedItems,
        grid: { ...state.grid, rows: state.grid.rows + DEFAULT_GRID_ROWS },
      };
    }
    case 'DELETE_PAGE_ABOVE': {
      const { pageNumber } = action.payload;
      if (state.grid.rows <= DEFAULT_GRID_ROWS) {
        return state;
      }
      const startRow = (pageNumber - 1) * DEFAULT_GRID_ROWS;
      const endRow = pageNumber * DEFAULT_GRID_ROWS;

      const remainingItems = state.items
        .filter(
          (item) => item.position.y < startRow || item.position.y >= endRow
        )
        .map((item) => {
          if (item.position.y >= endRow) {
            return {
              ...item,
              position: {
                ...item.position,
                y: item.position.y - DEFAULT_GRID_ROWS,
              },
            };
          }
          return item;
        });

      const nextRows = Math.max(
        DEFAULT_GRID_ROWS,
        state.grid.rows - DEFAULT_GRID_ROWS
      );

      return {
        ...state,
        items: remainingItems,
        grid: { ...state.grid, rows: nextRows },
      };
    }
    case 'DELETE_PAGE_BELOW': {
      const { pageNumber } = action.payload;
      if (state.grid.rows <= DEFAULT_GRID_ROWS) {
        return state;
      }
      const startRow = pageNumber * DEFAULT_GRID_ROWS;
      const endRow = (pageNumber + 1) * DEFAULT_GRID_ROWS;

      const remainingItems = state.items
        .filter(
          (item) => item.position.y < startRow || item.position.y >= endRow
        )
        .map((item) => {
          if (item.position.y >= endRow) {
            return {
              ...item,
              position: {
                ...item.position,
                y: item.position.y - DEFAULT_GRID_ROWS,
              },
            };
          }
          return item;
        });

      const nextRows = Math.max(
        DEFAULT_GRID_ROWS,
        state.grid.rows - DEFAULT_GRID_ROWS
      );

      return {
        ...state,
        items: remainingItems,
        grid: { ...state.grid, rows: nextRows },
      };
    }
    case 'REMOVE_ROWS': {
      const { rows } = action.payload;
      return {
        ...state,
        grid: { ...state.grid, rows: state.grid.rows - rows },
      };
    }
    case 'UPDATE_ITEM_COLOR': {
      const { id, color } = action.payload;
      const newItems = updateItemPlot(state.items, id, { color });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_COLOR_PALETTE': {
      const { id, colorPalette } = action.payload;
      const newItems = updateItemPlot(state.items, id, { colorPalette });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SHOW_DATA_LABELS': {
      const { id, showDataLabels } = action.payload;
      const newItems = updateItemPlot(state.items, id, { showDataLabels });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SORT': {
      const { id, sort } = action.payload;
      const newItems = updateItemPlot(state.items, id, { sort });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_BAR_AXIS_RANGE': {
      const { id, maxValue, minValue } = action.payload;
      const newItems = updateItemPlot(state.items, id, {
        maxValue,
        minValue,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_ORIENTATION': {
      const { id, orientation } = action.payload;
      const newItems = updateItemPlot(state.items, id, { orientation });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SHOW_ZEBRA_STRIPE': {
      const { id, showZebraStripe } = action.payload;
      const newItems = updateItemPlot(state.items, id, { showZebraStripe });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SHOW_QUARTILES': {
      const { id, showQuartiles } = action.payload;
      const newItems = updateItemPlot(state.items, id, { showQuartiles });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_ENABLE_ROW_SELECTION': {
      const { id, enableRowSelection } = action.payload;
      const newItems = updateItemPlot(state.items, id, { enableRowSelection });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_NORMALIZATION': {
      const { id, normalization } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          mapOptions: { ...(item.plot?.mapOptions ?? {}), normalization },
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_TRANSFORMATION': {
      const { id, transformation } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          mapOptions: { ...(item.plot?.mapOptions ?? {}), transformation },
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_REPORT_TITLE': {
      const { title } = action.payload;
      return { ...state, title };
    }
    case 'UPDATE_REPORT_DESCRIPTION': {
      const { description } = action.payload;
      return { ...state, description };
    }
    case 'ADD_ITEM_PLOT_PARAMETER': {
      const { id, parameterId } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const plotParameters = item.plot?.plotParameters ?? [];

        if (plotParameters.some((p) => p.parameterId === parameterId)) {
          return item;
        }

        const reportParameter = (state.reportParameters ?? []).find(
          (p) => p.parameterId === parameterId
        );

        // Comparison groups are usually grouped as one entity on line charts and split on other charts.
        const groupedByDefault =
          item.plot?.chartType === 'line' &&
          reportParameter?.isPrimary !== true;

        return {
          ...item,
          plot: {
            ...item.plot,
            plotParameters: [
              ...plotParameters,
              { parameterId, grouped: groupedByDefault },
            ],
          },
        };
      });
      return { ...state, items: newItems };
    }
    case 'REMOVE_ITEM_PLOT_PARAMETER': {
      const { id, parameterId } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const plotParameters = item.plot?.plotParameters ?? [];
        return {
          ...item,
          plot: {
            ...item.plot,
            plotParameters: plotParameters.filter(
              (p) => p.parameterId !== parameterId
            ),
          },
        };
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_PARAMETER_GROUPING': {
      const { id, parameterId, grouped } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const plotParameters = item.plot?.plotParameters ?? [];
        const updatedPlotParameters = plotParameters.map((p) =>
          p.parameterId === parameterId ? { ...p, grouped } : p
        );
        return {
          ...item,
          plot: {
            ...item.plot,
            plotParameters: updatedPlotParameters,
          },
        };
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_COMPARISON_TRANSFORMATION': {
      const { id, comparisonTransformation } = action.payload;
      const newItems = updateItemPlot(state.items, id, {
        comparisonTransformation,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DIVERGING_BAR_COMBO': {
      const { id, divergingBarCombo } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          divergingBarCombo: {
            ...item.plot?.divergingBarCombo,
            ...divergingBarCombo,
          },
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DIVERGING_BAR': {
      const { id, divergingBar } = action.payload;
      const newItems = updateItem(state.items, id, (item) => ({
        ...item,
        plot: {
          ...item.plot,
          divergingBar: {
            ...item.plot?.divergingBar,
            ...divergingBar,
          },
        },
      }));
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DISPLAY_AS': {
      const { id, displayAs } = action.payload;
      const newItems = updateItemPlot(state.items, id, { displayAs });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_TREEMAP_SIZE_TRANSFORM': {
      const { id, treemapSizeTransform } = action.payload;
      const existing = state.items.find((it) => it.id === id)?.plot?.treemap;
      const newItems = updateItemPlot(state.items, id, {
        treemap: { ...existing, sizeTransform: treemapSizeTransform },
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_CHORD_OPTIONS': {
      const { id, showTickLabels, tickLabelMode } = action.payload;
      const existing = state.items.find((it) => it.id === id)?.plot?.chord;
      const newItems = updateItemPlot(state.items, id, {
        chord: {
          ...existing,
          ...(showTickLabels !== undefined && { showTickLabels }),
          ...(tickLabelMode !== undefined && { tickLabelMode }),
        },
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SHOW_DIFF_LABEL': {
      const { id, showDiffLabel } = action.payload;
      const newItems = updateItemPlot(state.items, id, { showDiffLabel });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SHOW_LEGEND': {
      const { id, showLegend } = action.payload;
      const newItems = updateItemPlot(state.items, id, { showLegend });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_HIDE_OTHER': {
      const { id, hideOther } = action.payload;
      const newItems = updateItemPlot(state.items, id, { hideOther });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_ADAPTIVE': {
      const { id, adaptive } = action.payload;
      const newItems = updateItemPlot(state.items, id, { adaptive });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_CURVE_SMOOTHNESS': {
      const { id, curveSmoothness } = action.payload;
      const newItems = updateItemPlot(state.items, id, { curveSmoothness });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_FONT_SIZE': {
      const { id, fontSize } = action.payload;
      const newItems = updateItemPlot(state.items, id, { fontSize });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_DISTRIBUTION_CUTOFF': {
      const { id, distributionCutoffLower, distributionCutoffUpper } =
        action.payload;
      const newItems = updateItemPlot(state.items, id, {
        distributionCutoffLower,
        distributionCutoffUpper,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_FORMAT': {
      const { id, format } = action.payload;
      const newItems = updateItemPlot(state.items, id, { format });
      return { ...state, items: newItems };
    }
    case 'SET_SECONDARY_FILTERS': {
      return { ...state, secondaryFilters: action.payload.filters };
    }
    case 'UPDATE_ITEM_DATASOURCE': {
      const { id, dataSource } = action.payload;
      const newItems = updateItem(state.items, id, (item) => {
        const plot =
          dataSource === 'mw-gql'
            ? {
                ...item.plot,
                dataSource,
                snowflakeConfig: undefined,
              }
            : { ...item.plot, dataSource };
        if (dataSource === 'snowflake') {
          return {
            ...item,
            title: null,
            isTitleManuallySet: false,
            plot,
          };
        }
        return { ...item, plot };
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SNOWFLAKE_CONFIG': {
      const { id, snowflakeConfig, selectorOverrides } = action.payload;
      const newItems = updateItemPlot(state.items, id, {
        snowflakeConfig,
        selectorOverrides,
      });
      return { ...state, items: newItems };
    }
    case 'UPDATE_ITEM_SELECTOR_OVERRIDES': {
      const { id, selectorOverrides } = action.payload;
      const newItems = updateItemPlot(state.items, id, { selectorOverrides });
      return { ...state, items: newItems };
    }
    default:
      return state;
  }
};

type SetConfigAction = {
  type: 'SET_CONFIG';
  payload: Page;
};

type ApplyTabContentAction = {
  type: 'APPLY_TAB_CONTENT_ACTION';
  payload: { tabId: string; action: TabContentAction };
};

type UpdatePageTitleAction = {
  type: 'UPDATE_PAGE_TITLE';
  payload: string;
};

export type PageAction =
  | SetConfigAction
  | UpdatePageTitleAction
  | ApplyTabContentAction;

export const pageReducer = (state: Page, action: PageAction): Page => {
  switch (action.type) {
    case 'SET_CONFIG': {
      return action.payload;
    }

    case 'UPDATE_PAGE_TITLE': {
      return {
        ...state,
        name: action.payload,
      };
    }

    case 'APPLY_TAB_CONTENT_ACTION': {
      const { tabId, action: tabAction } = action.payload;
      const newTabs = state.tabs.map((tab) => {
        if (tab.id === tabId) {
          return {
            ...tab,
            tabContent: applyTabContentAction(tab.tabContent, tabAction),
          };
        }
        return tab;
      });

      return {
        ...state,
        tabs: newTabs,
      };
    }

    default: {
      return state;
    }
  }
};
